import React, { useCallback } from 'react';
import { DNAButton, DNABox, DNAText, DNACard, DNADivider, Iffy, util } from '@alucio/lux-ui';
import {
  DNAChip,
  DNACustomFieldChipList,
  DNADocumentStatusChip,
  DNADocumentThumbnail,
  DNACardComponent,
  DNADocumentVersionContextMenu,
  useDNADocumentVersionActions,
  useCurrentPage,
  DNAFolderStatusChip,
} from 'src/components/DNA';
import { DocumentVersionORM, FolderItemORM } from 'src/types/types';
import { useAppSettings } from 'src/state/context/AppSettings';
import { detectArchivedFileKeyPath } from 'src/components/SlideSelector/useThumbnailSelector';
import { GRID_CARD_STYLES_VARIANTS_CONFIGS } from './constants';
import { styles } from './Styles';

export const DNADocumentFolderItemCard: DNACardComponent<FolderItemORM> = (props) => {
  const { ORM: folderItemORM, isOnline, onPress } = props
  const documentVerActions = useDNADocumentVersionActions()
  const documentVersionORM = folderItemORM.relations.itemORM as DocumentVersionORM
  const parentDocumentORM = documentVersionORM.relations.documentORM
  const isBookmarked = parentDocumentORM.meta.bookmark.isBookmarked
  // [TODO-2126] - We should have a property that combines isOnline and isContentCached
  //               on the DocumentVersionORM rather than checking both at the component level
  const unavailableContent = !isOnline && !documentVersionORM.meta.assets.isContentCached;
  const { deviceMode } = useAppSettings();
  const isDesktop = deviceMode === 'desktop';
  const route = useCurrentPage({ exact: false });
  const isPublisherRoute = route?.configOptions?.modules?.includes('publisher')
  const canPresent = documentVersionORM.relations.documentORM.meta.permissions.MSLPresent
  const defaultOnPress = canPresent
    ? documentVerActions.present(documentVersionORM, folderItemORM)
    : documentVerActions.preview(documentVersionORM, folderItemORM)
  const handleOnPress = useCallback(() => {
    if (!unavailableContent) onPress?.(folderItemORM)
  }, [onPress, folderItemORM, unavailableContent])

  // [TODO-2126] - This thumbnail key should be calculated at the ORM level
  let customThumbnailKey: string | undefined
  const selectedThumbnail = documentVersionORM.model.selectedThumbnail ?? 1
  if (folderItemORM.model.visiblePages && !folderItemORM.model.visiblePages.find(vp => vp === selectedThumbnail)) {
    const customPage = documentVersionORM.meta.allPages[folderItemORM.model.visiblePages[0] - 1]
    customThumbnailKey = detectArchivedFileKeyPath(documentVersionORM.model, customPage)
  }
  const isDraft = documentVersionORM.relations.documentORM.meta.hasUnpublishedVersion &&
  !documentVersionORM.meta.schedule.publish.isScheduled
  return (
    <DNACard
      testID="folder-item-document-card-container"
      appearance="outline"
      interactive="pressable"
      onPress={handleOnPress}
      style={util.mergeStyles(
        undefined,
        styles.cardContainerDesktop,
        [styles.cardContainerTablet, !isDesktop],
        [styles.unavailableContent, unavailableContent],
      )}
    >
      {/* ACTION BUTTONS */}
      <DNABox spacing="between">
        <DNAButton
          appearance="ghost"
          status={isBookmarked ? 'primary' : 'subtle'}
          size={isDesktop ? 'md' : 'xl'}
          iconLeft={isBookmarked ? 'star-fill' : 'star'}
          onPress={documentVerActions.bookmark(documentVersionORM)}
        />
        <DNADocumentVersionContextMenu folderItemORM={folderItemORM}>
          <DNAButton
            appearance="ghost"
            status="subtle"
            size={isDesktop ? 'md' : 'xl'}
            iconLeft="dots-three-vertical-bold"
          />
        </DNADocumentVersionContextMenu>
      </DNABox>
      {/* THUMBNAIL & TITLE & CHIPS */}
      <DNABox fill childFill={1} appearance="col" spacing="xs">
        {/* THUMBNAIL */}
        <DNADocumentThumbnail
          documentVersionORM={documentVersionORM}
          width={GRID_CARD_STYLES_VARIANTS_CONFIGS.THUMBNAIL[deviceMode].width}
          height={GRID_CARD_STYLES_VARIANTS_CONFIGS.THUMBNAIL[deviceMode].height}
          customThumbnailKey={customThumbnailKey}
          unavailableContent={unavailableContent}
          onPress={isPublisherRoute ? handleOnPress : defaultOnPress}
          // [TODO] - Implement here - Nonhover
        />
        {/* TITLE */}
        <DNABox fill style={styles.cardTitleContainer}>
          <DNABox fill alignY="center">
            <DNAText testID="document-card-title" style={styles.cardTitle} numberOfLines={2}>
              {folderItemORM.meta.title}
            </DNAText>
          </DNABox>
        </DNABox>
        <DNADivider />
        {/* CHIPS */}
        <DNABox alignY="center" spacing="xs" style={styles.cardChipsContainer}>
          <Iffy is={unavailableContent}>
            <DNAChip.Beacon
              testID={`content-not-available-${folderItemORM.meta.title}`}
              variant="CONTENT_UNAVAILABLE"
            />
          </Iffy>
          <Iffy is={!unavailableContent}>
            <DNACustomFieldChipList documentVersionORM={documentVersionORM} />
            <Iffy is={isPublisherRoute}>
              <DNADocumentStatusChip
                documentStatus={documentVersionORM.relations.documentORM.model.status}
                isScheduled={documentVersionORM.meta.schedule.publish.isScheduled}
                isDraft={isDraft}
              />
            </Iffy>
            <DNAFolderStatusChip orm={folderItemORM} variant="List"/>
          </Iffy>
        </DNABox>
      </DNABox>
    </DNACard>
  );
}

export default DNADocumentFolderItemCard
